import { Toast } from "@hubrise/react-components"
import * as Sentry from "@sentry/react"
import { QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import * as React from "react"
import { I18nextProvider } from "react-i18next"
import { BrowserRouter } from "react-router-dom"
import { ThemeProvider } from "styled-components"

import { ConfigContextProvider } from "shared/components/ConfigContext"
import DragDropPreventer from "shared/components/DragDropPreventer"
import { EditorStoreContextProvider } from "shared/components/EditorStoreContext"
import { KeyboardContextProvider } from "shared/components/KeyboardContext"
import { NavigationContextProvider } from "shared/components/NavigationContext"
import PageError from "shared/components/PageError"
import { queryClient } from "shared/hooks/queries"
import useFavicon from "shared/hooks/useFavicon"
import { BaseStyles } from "shared/styles/BaseStyles"
import { ExtraNormalizeStyles } from "shared/styles/ExtraNormalizeStyles"
import { NormalizeStyles } from "shared/styles/NormalizeStyles"
import { appInstanceIdFromUrl, redirectTo } from "shared/utils/browser"
import { ServerConfig } from "shared/utils/config"
import i18n from "shared/utils/i18n"
import { theme } from "shared/utils/theme"

import AppRoutes from "./AppRoutes"

import "shared/styles/FontStyle.css"

const App = ({ serverConfig }: { serverConfig: ServerConfig }): JSX.Element | null => {
  useFavicon(serverConfig.images.favicon)

  const appInstanceId = appInstanceIdFromUrl()
  if (appInstanceId === null) {
    redirectTo(serverConfig.connectionsUrl)
    return null
  }

  return (
    <React.Fragment>
      <NormalizeStyles />
      <ExtraNormalizeStyles />
      <BaseStyles />

      <QueryClientProvider client={queryClient}>
        <ConfigContextProvider serverConfig={serverConfig} appInstanceId={appInstanceId}>
          <I18nextProvider i18n={i18n}>
            <KeyboardContextProvider>
              <EditorStoreContextProvider>
                <ThemeProvider theme={theme(serverConfig)}>
                  <Toast />

                  <BrowserRouter>
                    <NavigationContextProvider>
                      <DragDropPreventer>
                        <Sentry.ErrorBoundary fallback={<PageError />}>
                          <AppRoutes />
                        </Sentry.ErrorBoundary>
                      </DragDropPreventer>
                    </NavigationContextProvider>
                  </BrowserRouter>

                  <ReactQueryDevtools />
                </ThemeProvider>
              </EditorStoreContextProvider>
            </KeyboardContextProvider>
          </I18nextProvider>
        </ConfigContextProvider>
      </QueryClientProvider>
    </React.Fragment>
  )
}

export default App
